import OpenAI from 'openai';

interface QuestionAnalysis {
  features: number[];
  type: string;
  complexity: number;
  topics: string[];
}

// Initialize OpenAI
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

// Extract features from question text
const extractFeatures = (text: string): number[] => {
  const features = [];
  
  // Length-based features
  features.push(text.length / 1000); // Normalized length
  features.push(text.split(' ').length / 100); // Word count
  
  // Complexity indicators
  features.push(text.split('?').length - 1); // Number of questions
  features.push((text.match(/\b(how|why|what|when|where|who)\b/gi) || []).length); // Question words
  features.push((text.match(/\b(explain|describe|analyze|compare|evaluate)\b/gi) || []).length); // Analysis words
  
  // Topic indicators
  features.push((text.match(/\b(math|calculate|equation|number)\b/gi) || []).length);
  features.push((text.match(/\b(money|finance|invest|budget)\b/gi) || []).length);
  features.push((text.match(/\b(science|physics|chemistry|biology)\b/gi) || []).length);
  
  return features;
};

// Analyze question complexity and type
export const analyzeQuestion = async (question: string): Promise<QuestionAnalysis> => {
  try {
    // Extract basic features
    const features = extractFeatures(question);
    
    // Get AI analysis of question
    const completion = await openai.chat.completions.create({
      messages: [
        {
          role: 'system',
          content: `Analyze the following question and return a JSON object with:
          - type: question type (factual, conceptual, analytical, etc.)
          - complexity: number 1-10
          - topics: array of relevant topics`
        },
        { role: 'user', content: question }
      ],
      model: 'gpt-3.5-turbo',
      temperature: 0,
      response_format: { type: "json_object" }
    });

    const analysis = JSON.parse(completion.choices[0]?.message?.content || '{}');

    return {
      features,
      type: analysis.type || 'unknown',
      complexity: analysis.complexity || 5,
      topics: analysis.topics || []
    };
  } catch (error) {
    console.error('Error analyzing question:', error);
    // Return basic analysis if AI fails
    return {
      features,
      type: 'unknown',
      complexity: 5,
      topics: []
    };
  }
};